/**
 * DEVLOGS-TEMP — browser adapter (web app, web player, desktop renderer).
 *
 * The caller passes `env` rather than this module reading `import.meta.env`
 * directly: these files are also loaded by the Node test runner, where that
 * construct does not exist (the same guarded-read reason as
 * artifacts/web/src/player/lib/client-report.ts).
 */

import { installConsoleCapture } from "../capture/console.ts";
import { installErrorCapture } from "../capture/errors.ts";
import { installFetchCapture, installXhrCapture } from "../capture/net.ts";
import { installMediaCapture } from "../capture/media.ts";
import { installWebSocketCapture } from "../capture/ws.ts";
import { resolveDevLogConfig, type EnvRecord } from "../config.ts";
import { installDevLog, type DevLogHandle } from "../install.ts";
import { newSpanId } from "../session.ts";
import type { DevLogSurface } from "../types.ts";

export interface WebDevLogOptions {
  env: EnvRecord;
  surface: Extract<DevLogSurface, "web-player" | "web-panel" | "desktop">;
  /** `import.meta.env.PROD`. See the production-guard note in config.ts. */
  isProduction: boolean;
  /** Hosts to skip in the fetch capture (Sentry/PostHog ingest). */
  ignoreHosts?: readonly string[];
  label?: string;
}

/** Session override: `?devlog=1` or localStorage. Cannot enable transport. */
const OVERRIDE_KEY = "omnimedia:devlog";

function readOverride(): { level?: string; ns?: string } | null {
  try {
    const g = globalThis as {
      location?: { search?: string };
      localStorage?: Storage;
      sessionStorage?: Storage;
    };
    const params = new URLSearchParams(g.location?.search ?? "");
    const flag = params.get("devlog");
    if (flag === "1") {
      try {
        g.sessionStorage?.setItem(OVERRIDE_KEY, "1");
      } catch {
        /* private mode */
      }
    }
    const ns = params.get("devlog_ns");
    const active =
      flag === "1" ||
      g.sessionStorage?.getItem(OVERRIDE_KEY) === "1" ||
      g.localStorage?.getItem(OVERRIDE_KEY) === "1";
    if (!active && !ns) return null;
    return { level: active ? "trace" : undefined, ns: ns ?? (active ? "*" : undefined) };
  } catch {
    return null;
  }
}

export function installWebDevLog(options: WebDevLogOptions): DevLogHandle | null {
  const g = globalThis as unknown as {
    fetch?: typeof fetch;
    console?: Console;
    navigator?: { sendBeacon?: (url: string, data?: BodyInit) => boolean };
    document?: { addEventListener?: (t: string, l: () => void) => void; visibilityState?: string };
    addEventListener?: (t: string, l: () => void) => void;
    XMLHttpRequest?: never;
    WebSocket?: typeof WebSocket;
    HTMLMediaElement?: typeof HTMLMediaElement;
  };

  if (typeof g.fetch !== "function") return null;

  const config = resolveDevLogConfig(options.env, {
    surface: options.surface,
    isProduction: options.isProduction,
    prefix: "VITE_",
    override: readOverride(),
  });

  // Capture the pristine fetch BEFORE anything wraps it — loop guard #1.
  const pristineFetch = g.fetch.bind(globalThis);

  const handle = installDevLog({
    config,
    surface: options.surface,
    fetchImpl: pristineFetch,
    platform: readPlatform(),
    label: options.label,
    beacon: g.navigator?.sendBeacon
      ? (url, body) => {
          try {
            return g.navigator?.sendBeacon?.(url, body) ?? false;
          } catch {
            return false;
          }
        }
      : null,
  });
  if (handle === null) return null;

  const emit = handle.logger.log.bind(handle.logger);
  const teardown: Array<() => void> = [];
  const netOptions = {
    emit,
    now: Date.now,
    newSpan: newSpanId,
    ignoreUrls: handle.ignoreUrls,
    ignoreHosts: options.ignoreHosts,
    isSending: handle.isSending,
  };

  if (g.console) teardown.push(installConsoleCapture(g.console, emit));
  teardown.push(
    installErrorCapture(globalThis as never, emit, { onFlush: () => handle.flush() }),
  );
  teardown.push(installFetchCapture(globalThis as { fetch: typeof fetch }, netOptions));
  teardown.push(installXhrCapture(globalThis as never, netOptions));
  if (g.WebSocket) {
    teardown.push(
      installWebSocketCapture(globalThis as { WebSocket: typeof WebSocket }, {
        emit,
        now: Date.now,
      }),
    );
  }
  if (g.HTMLMediaElement) {
    teardown.push(
      installMediaCapture(globalThis as { HTMLMediaElement?: typeof HTMLMediaElement }, {
        emit,
        now: Date.now,
      }),
    );
  }

  // Last chance to ship what is queued.
  const onHidden = (): void => {
    try {
      if (g.document?.visibilityState === "hidden") handle.flush();
    } catch {
      /* fire-and-forget */
    }
  };
  try {
    g.document?.addEventListener?.("visibilitychange", onHidden);
    g.addEventListener?.("pagehide", () => handle.flush());
  } catch {
    /* fire-and-forget */
  }

  return {
    ...handle,
    uninstall: () => {
      for (const fn of teardown) {
        try {
          fn();
        } catch {
          /* fire-and-forget */
        }
      }
      handle.uninstall();
    },
  };
}

function readPlatform(): string | undefined {
  try {
    const nav = (globalThis as { navigator?: { userAgent?: string } }).navigator;
    return nav?.userAgent?.slice(0, 120);
  } catch {
    return undefined;
  }
}
